import { useState, useMemo } from "react";
import styles from "../../styles/portfolioManagerDashboard.module.scss";

import { TriangleAlert } from "lucide-react";
import {
  ComposedChart,
  Area,
  Line,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { displayDecimalFormatted, getAccountCurrency } from "../../../../utils/systemSettings";
import KpiInfoIcon from "./KpiInfoIcon";
import { KPI_INFO, formatCompactMoney } from "./kpiHelpers";

const BLUE = "#342c82";
const BURN_COLOR = "#e97a00";
const SPEND_COLOR = "#e97a00";

const settings = JSON.parse(localStorage.getItem("settings"));
const currency = getAccountCurrency(settings);

const formatDecimal = (val) => `${displayDecimalFormatted(settings, val ?? 0)}`;
const formatDecimalMoney = (val) => formatCompactMoney(settings, currency, val);

const SERIES = [
  { key: "planned", label: "Planned Value", color: BLUE, dashed: true },
  { key: "actual",  label: "Actual Value",  color: BLUE, dashed: false },
  { key: "burnRate", label: "Burn Rate %",  color: BURN_COLOR, dashed: false },
];



function LegendLine({ color, dashed }) {
  return (
    <svg width={16} height={10} style={{ flexShrink: 0 }}>
      <line
        x1={0} y1={5} x2={16} y2={5}
        stroke={color}
        strokeWidth={2}
        strokeDasharray={dashed ? "4 2" : undefined}
      />
    </svg>
  );
}

function CustomXTick({ x, y, payload, data }) {
  const point = data?.find((d) => d.label === payload.value);
  const hasAnomaly = point && point.overSpend > 0;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={12} textAnchor="middle" fontSize={10} fill="#9e9e9e" fontWeight={500}>
        {payload.value}
      </text>
      {hasAnomaly && (
        <foreignObject x={-7} y={16} width={14} height={14}>
          <TriangleAlert size={12} color="#dc2626" strokeWidth={2.5} />
        </foreignObject>
      )}
    </g>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const overSpend = payload[0]?.payload?.overSpend;
  return (
    <div className={styles.pvTooltip}>
      <span className={styles.pvTooltipLabel}>{label}</span>
      {payload.map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: entry.color, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: "#666" }}>{entry.name}:</span>
          <span className={styles.pvTooltipValue} style={{ fontSize: 13 }}>
            {entry.name === "Burn Rate %" ? `${formatDecimal(entry.value)}%` : formatDecimalMoney(entry.value)}
          </span>
        </div>
      ))}
      {overSpend > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, paddingTop: 6, borderTop: "1px solid #f0f0f0", color: "#dc2626" }}>
          <TriangleAlert size={12} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>Over Spend: {formatDecimalMoney(overSpend)}</span>
        </div>
      )}
    </div>
  );
}

function PortfolioValuePanel({
  metricValue,
  chartData = [],
}) {
  const [visible, setVisible] = useState({ planned: true, actual: true, burnRate: true, monthlySpend: true });

  const toggle = (key) => setVisible((prev) => ({ ...prev, [key]: !prev[key] }));

  const hasSpendData = chartData.some((d) => d.monthlySpend != null && d.monthlySpend > 0);

  const { processedData, scale } = useMemo(() => {
    const data = chartData.map((d) => ({
      ...d,
      overSpend: Math.max(0, (d.planned ?? 0) - (d.actual ?? 0)),
    }));
    const maxVal = Math.max(
      0,
      ...chartData.flatMap((d) => [d.planned ?? 0, d.actual ?? 0, d.monthlySpend ?? 0])
    );
    const sc =
      maxVal >= 1_000_000
        ? { divisor: 1_000_000, suffix: "M" }
        : maxVal >= 1_000
        ? { divisor: 1_000, suffix: "K" }
        : null;
    return { processedData: data, scale: sc };
  }, [chartData]);

  const formatAxisMoney = (val) =>
    scale
      ? `${currency} ${displayDecimalFormatted(settings, val / scale.divisor)}${scale.suffix}`
      : formatDecimalMoney(val);

  return (
    <div className={styles.analyticsPanel}>
      <KpiInfoIcon content={KPI_INFO.valueDelivered} />
      <div className={styles.pvHeader}>
        <div className={styles.pvKpiBlock}>
          <span className={styles.label}>Value Delivered & Budget Burn Rate</span>
          <div className={styles.pvKpiRow}>
            {metricValue && <span className={styles.pvKpiValue}>{metricValue}</span>}
          </div>
        </div>
      </div>

      {/* Legend — clickable */}
      <div className={styles.chartLegend}>
        {SERIES.map(({ key, label, color, dashed }) => (
          <span
            key={key}
            className={styles.legendItem}
            onClick={() => toggle(key)}
            style={{ cursor: "pointer", opacity: visible[key] ? 1 : 0.35, userSelect: "none" }}
          >
            <LegendLine color={color} dashed={dashed} />
            {label}
          </span>
        ))}
        {hasSpendData && (
          <span
            className={styles.legendItem}
            onClick={() => toggle("monthlySpend")}
            style={{ cursor: "pointer", opacity: visible.monthlySpend ? 1 : 0.35, userSelect: "none" }}
          >
            <span className={styles.legendDot} style={{ background: SPEND_COLOR, opacity: 0.3 }} />
            Monthly Spend
          </span>
        )}
      </div>

      <div className={styles.pvBody}>
        <div className={styles.pvChartCol}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={processedData} margin={{ top: 4, right: 8, bottom: 20, left: 0}}>
              <defs>
                <linearGradient id="pvPlannedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BLUE} stopOpacity={0.08} />
                  <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="label"
                tick={<CustomXTick data={processedData} />}
                axisLine={false}
                tickLine={false}
              />
              {/* Left Y-axis — dollar values */}
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 10, fill: "#9e9e9e" }}
                axisLine={false}
                tickLine={false}
                width={60}
                tickFormatter={formatAxisMoney}
              />
              {/* Right Y-axis — burn rate % */}
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 10, fill: BURN_COLOR }}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
                width={42}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#c4c4c4", strokeWidth: 1, strokeDasharray: "4 3" }} />

              {/* Monthly spend bars (behind lines) */}
              {hasSpendData && visible.monthlySpend && (
                <Bar
                  yAxisId="left"
                  dataKey="monthlySpend"
                  name="Monthly Spend"
                  fill={SPEND_COLOR}
                  fillOpacity={0.15}
                  stroke={SPEND_COLOR}
                  strokeOpacity={0.25}
                  radius={[3, 3, 0, 0]}
                  barSize={16}
                />
              )}

              {/* Planned Value — dashed blue area */}
              {visible.planned && (
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="planned"
                  name="Planned Value"
                  stroke={BLUE}
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                  fill="url(#pvPlannedGrad)"
                  dot={false}
                  activeDot={{ r: 3, fill: BLUE, stroke: "#fff", strokeWidth: 2 }}
                />
              )}

              {/* Actual Value — solid blue line */}
              {visible.actual && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="actual"
                  name="Actual Value"
                  stroke={BLUE}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 4, fill: BLUE, stroke: "#fff", strokeWidth: 2 }}
                />
              )}

              {/* Burn Rate % — solid orange line on right axis */}
              {visible.burnRate && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="burnRate"
                  name="Burn Rate %"
                  stroke={BURN_COLOR}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: BURN_COLOR, stroke: "#fff", strokeWidth: 2 }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default PortfolioValuePanel;

